import React, { useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Button,
} from "@mui/material";
import { makeStyles } from "../../../utils/makeStyles";
import SyncIcon from "@mui/icons-material/Sync";
import { CommonUtil, readJson } from "../../../utils/CommonUtil";
import { NetworkManager } from "../../../Components/NetworkManager";
import ToastManager from "../../../Components/ToastManager";
import axios from "axios";

interface Audience {
  customAudienceId: number;
  customAudienceName: string;
  customAudienceDesc: string;
  audienceSize: number;
  syncStatus: string;
  uploadStatus?: string;
  syncPending?: boolean;
}

interface Props {
  audiences: Audience[];
  onRefresh: () => void;
}

const useStyles = makeStyles()({
  syncButton: {
    backgroundColor: "#fd6c33",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#fd6c33",
      boxShadow: "none",
    },
  },
  syncingButton: {
    backgroundColor: "#1DA1F2",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1DA1F2",
      boxShadow: "none",
    },
  },
  syncScheduledButton: {
    backgroundColor: "#f59e0b",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#f59e0b",
      boxShadow: "none",
    },
  },
  uploadButton: {
    backgroundColor: "#1DA1F2",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  uploadingButton: {
    backgroundColor: "#6B7280",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    opacity: 0.6,
    "&:hover": {
      backgroundColor: "#6B7280",
      boxShadow: "none",
    },
  },
});

const AudienceList: React.FC<Props> = ({ audiences, onRefresh }) => {
  const { classes } = useStyles();
  const [syncingAudiences, setSyncingAudiences] = useState<Set<number>>(
    new Set(),
  );
  const [uploadingAudienceId, setUploadingAudienceId] = useState<number | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingAudienceIdRef = useRef<number | null>(null);

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";

  const handleSyncClick = (audienceId: number) => {
    setSyncingAudiences((prev) => new Set(prev).add(audienceId));
    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}&customAudienceId=${audienceId}`;
    try {
      NetworkManager.sendJsonRequest(
        "broadcastCustomAudiences/syncAudiences",
        "",
        pageDataReq,
        "POST",
        true,
        (data: any) => {
          const respData = CommonUtil.parseData(data);

          if (respData?.statusCode === "BM0000") {
            ToastManager.showToast("Sync started successfully", "Success");
          } else {
            ToastManager.showToast("Failed to start sync.", "Error");
            // Remove from syncing state if failed
            setSyncingAudiences((prev) => {
              const updated = new Set(prev);
              updated.delete(audienceId);
              return updated;
            });
          }
        },
        (error: any) => {
          ToastManager.showToast("Error while syncing audience.", "Error");
          // Remove from syncing state on error
          setSyncingAudiences((prev) => {
            const updated = new Set(prev);
            updated.delete(audienceId);
            return updated;
          });
        },
      );
    } catch (error) {
      ToastManager.showToast("Unexpected error occurred.", "Error");

      setSyncingAudiences((prev) => {
        const updated = new Set(prev);
        updated.delete(audienceId);
        return updated;
      });
    }
  };

  const handleUploadClick = (audienceId: number) => {
    pendingAudienceIdRef.current = audienceId;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected later
    if (!file || pendingAudienceIdRef.current == null) return;

    const capturedAudienceId = pendingAudienceIdRef.current;
    pendingAudienceIdRef.current = null;
    setUploadingAudienceId(capturedAudienceId);
    const url =
      `${NetworkManager.serverPath}broadcastCustomAudiences/uploadUsers` +
      `?pageId=${encodeURIComponent(pageId)}` +
      `&adAccountId=${encodeURIComponent(adAccountId)}` +
      `&customAudienceId=${encodeURIComponent(String(capturedAudienceId))}`;

    const formData = new FormData();
    formData.append("file", file);

    ToastManager.showToast("Uploading Started Successfully", "Success");

    axios
      .post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("cognitoAccessToken")}`,
        },
      })
      .then((response) => {
        if (response.data?.statusCode === "BM0000") {
          onRefresh();
        } else {
          ToastManager.showToast(
            response.data?.message ?? "Upload failed",
            "Error",
          );
        }
      })
      .catch(() => ToastManager.showToast("Upload failed", "Error"))
      .finally(() => setUploadingAudienceId(null));
  };

  const uploadChipLabel = (a: Audience): string | null => {
    if (uploadingAudienceId === a.customAudienceId) return "Uploading...";
    if (a.uploadStatus === "Complete") return "Upload Completed";
    if (a.uploadStatus === "Failed") return "Upload Failed";
    if (a.uploadStatus === "Uploading") return "Uploading...";
    return null;
  };

  const uploadChipColor = (a: Audience): string => {
    if (uploadingAudienceId === a.customAudienceId) return "#6B7280";
    if (a.uploadStatus === "Failed") return "#dc2626";
    if (a.uploadStatus === "Complete") return "#16a34a";
    return "#6B7280";
  };

  return (
    <>
      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {audiences && audiences.length > 0 ? (
        audiences.map((a) => (
          <Card
            key={a.customAudienceId}
            variant="outlined"
            sx={{
              borderRadius: 3,
              mb: 2,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
              "&:hover": {
                boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              },
            }}>
            <CardContent sx={{ padding: "16px !important" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <Box sx={{ textAlign: "start", flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                    {a.customAudienceName}
                  </Typography>

                  <Typography noWrap sx={{ fontSize: 14, color: "#6B7280", mt: 0.5 }}>
                    {a.customAudienceDesc}
                  </Typography>

                  {uploadChipLabel(a) && (
                    <Chip
                      label={uploadChipLabel(a)}
                      size="small"
                      sx={{
                        mt: 0.75,
                        backgroundColor: uploadChipColor(a),
                        color: "#fff",
                        fontSize: 11,
                        height: 20,
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 14, color: "#6B7280", mr: 2 }}>
                    {a.audienceSize} people
                  </Typography>

                  <Button
                    variant="contained"
                    className={
                      uploadingAudienceId === a.customAudienceId
                        ? classes.uploadingButton
                        : classes.uploadButton
                    }
                    disabled={uploadingAudienceId === a.customAudienceId}
                    onClick={() => handleUploadClick(a.customAudienceId)}>
                    Upload Users
                  </Button>

                  {(() => {
                    // "Syncing" can come from a manual click (local set) or from a
                    // backend-triggered sync (e.g. the auto sync after a CSV upload),
                    // which never touches the local set. Honor both.
                    const isSyncing =
                      syncingAudiences.has(a.customAudienceId) ||
                      a.syncStatus === "Syncing";
                    const isScheduled = a.syncStatus === "Sync Scheduled";
                    return (
                      <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        className={
                          isSyncing
                            ? classes.syncingButton
                            : isScheduled
                              ? classes.syncScheduledButton
                              : classes.syncButton
                        }
                        disabled={isSyncing || isScheduled}
                        onClick={() => handleSyncClick(a.customAudienceId)}>
                        {isSyncing
                          ? "Syncing..."
                          : isScheduled
                            ? "Sync Scheduled"
                            : "Sync"}
                      </Button>
                    );
                  })()}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            mb: 2,
            borderColor: "#E5E7EB",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          }}>
          <CardContent>
            <Typography sx={{ textAlign: "center", color: "#6B7280" }}>
              No Custom Audiences
            </Typography>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AudienceList;
